import JustValidate from 'just-validate'
import JustValidatePluginDate from 'just-validate-plugin-date'
import { select, selectAll } from 'helpers/dom'

const selectors = {
  ERROR_MESSAGE_SELECTOR: '.js-input-error-message'
}

const getMinDate = () => {
  const today = new Date()

  today.setDate(today.getDate() + 7)

  return today.toISOString().split('T')[0]
}

const settings = {
  errorFieldCssClass: 'is-invalid',
  errorLabelCssClass: 'input-error js-input-error-message',
  errorLabelStyle: {
    color: '#DC2626'
  },
  focusInvalidField: true,
  lockForm: false
}

const allRules = {
  email: {
    rule: 'email',
    errorMessage: 'Email is invalid!'
  },
  password: {
    validator: (value) => {
      if (value.length >= 6) return true

      return false
    },
    errorMessage: 'Password must be at least 6 characters'
  },
  number: {
    rule: 'number'
  },
  tel: {
    validator: (value) => {
      const regex = /\(?([0-9]{3})\)?([ .-]?)([0-9]{3})\2([0-9]{4})/

      if (!value) return true
      if (regex.test(value)) return true

      return false
    },
    errorMessage: 'Phone number is invalid'
  },
  trialDateSelect: {
    plugin: JustValidatePluginDate(() => ({
      isBefore: '2400/01/01',
      isAfter: getMinDate()
    })),
    errorMessage: 'Date must be after 7 days of current date'
  },
  postcode: {
    validator: (value) => {
      const regex = /\^(0[289][0-9]{2})$|^([1-9][0-9]{3})$/

      if (regex.test(value) && value.length <= 4) return true

      return false
    },
    errorMessage: 'Postcode is invalid'
  }
}

/**
 * Initializes validation for a form.
 * @param {HTMLElement} formEl - The form element.
 * @param {Boolean} callback - The callback function to be executed after successful validation.
 * @param {Boolean} autoSubmit - Whether to automatically submit the form after successful validation.
 * @throws {Error} If formEl is not a valid HTMLElement.
 * @returns {JustValidate} The JustValidate instance.
 */

const initValidate = (formEl, callback = false, autoSubmit = true) => {
  // Check if formEl is a valid DOM element and has an id property
  if (!(formEl instanceof HTMLElement)) {
    throw new Error('formEl must be a valid DOM element with an id property')
  }

  const inputs = selectAll('input:not([type=hidden]), select, textarea', formEl)

  if (inputs.length) {
    const validate = new JustValidate(formEl, settings)
    const newRules = inputs.map((input) => getValidateRule(input))

    newRules.forEach((rule) => rule.rules.length > 0 && validate.addField(rule.id, rule.rules))

    if (autoSubmit && !callback) {
      validate.onSuccess((event) => {
        formEl.submit(event)
      })

      validate.onFail((fields) => {
        Object.values(fields).forEach((field) => {
          const fieldEl = field.elem
          const errorMessageEl = select(selectors.ERROR_MESSAGE_SELECTOR, fieldEl.parentElement)

          if (!fieldEl.hasAttribute('id')) {
            return
          }

          if (!errorMessageEl) {
            fieldEl.removeAttribute('aria-describedby')
          } else {
            const errorId = `error-${fieldEl.id}`

            errorMessageEl.id = errorId
            fieldEl.setAttribute('aria-describedby', errorId)
          }
        })
      })
    }

    return validate
  }

  return null
}

/**
 * Adds a confirmation rule to an input field.
 * @param {Object} rules - The validation rules object.
 * @param {HTMLElement} input - The input element.
 * @returns {Object} The updated validation rules object.
 * @throws {Error} If rules or input is not a valid object or HTMLElement respectively.
 */

const addRuleConfirm = (rules, input) => {
  const confirmField = input.getAttribute('data-confirm')
  const confirmMessage = input.getAttribute('data-confirm-message')

  rules.push({
    validator: (value, fields) => {
      if (fields[confirmField] && fields[confirmField].elem) {
        const confirmFieldValue = fields[confirmField].elem.value

        return value === confirmFieldValue
      }

      return true
    },
    errorMessage: confirmMessage
  })

  return rules
}

/**
 * Generates validation rules for a given input.
 * @param {HTMLElement} input - The input element.
 * @returns {Object} The validation rules.
 * @throws {Error} If the input is not a valid HTMLElement.
 */

const getValidateRule = (input) => {
  if (!(input instanceof HTMLElement)) {
    throw new Error('Invalid input for getValidateRule')
  }

  if (!input.id) {
    throw new Error('Form input must have id')
  }

  const inputName = input.getAttribute('aria-label') || 'Field'
  const rules = {
    id: `#${input.id}`,
    rules: []
  }

  if (input.required) {
    rules.rules.push({
      rule: 'required',
      errorMessage: `${inputName} is required`
    })
  }

  if (input.type === 'email') {
    rules.rules.push(allRules.email)
  }

  if (input.type === 'password') {
    rules.rules.push(allRules.password)
  }

  if (input.type === 'number') {
    rules.rules.push(allRules.number)
  }

  if (input.type === 'tel') {
    rules.rules.push(allRules.tel)
  }

  if (input.max) {
    rules.rules.push({
      rule: 'maxLength',
      value: parseInt(input.max)
    })
  }

  if (input.min) {
    rules.rules.push({
      rule: 'minLength',
      value: parseInt(input.min)
    })
  }

  if (input.name === 'postcode') {
    rules.rules.push(allRules.postcode)
  }

  if (input.hasAttribute('data-confirm') && input.hasAttribute('data-confirm-message')) {
    rules.rules = addRuleConfirm(rules.rules, input)
  }

  if (input.name === 'validate-7-days-after-current') {
    rules.rules.push(allRules.trialDateSelect)
  }

  return rules
}

export { initValidate }
